/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import { TextField, InputAdornment, TextFieldProps } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

/**
 * Props for CurrencyInput component.
 * @interface CurrencyInputProps
 * @extends {Omit<TextFieldProps, 'value' | 'onChange'>}
 * @property {number | string} [value] - Currency value
 * @property {(value: number) => void} [onChange] - Value change handler
 * @property {string} [currency] - Currency code (default: 'USD')
 * @property {boolean} [showCurrencySymbol] - Show $ symbol (default: true)
 * @property {number} [maxValue] - Max value (default: 999999999.99)
 * @property {number} [minValue] - Min value (default: 0)
 * @property {boolean} [allowNegative] - Allow negatives (default: false)
 * @property {'onBlur' | 'immediate'} [onChangeMode] - Change trigger mode (default: 'onBlur')
 */
interface CurrencyInputProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  value?: number | string;
  onChange?: (value: number) => void;
  currency?: string;
  showCurrencySymbol?: boolean;
  maxValue?: number;
  minValue?: number;
  allowNegative?: boolean;
  onChangeMode?: 'onBlur' | 'immediate';
}

/**
 * Currency input with automatic formatting and validation.
 * Formats values with currency symbol, thousand separators, and 2 decimals.
 * 
 * Features:
 * - Currency symbol prefix ($)
 * - Thousand separators (1,234.56)
 * - Two decimal precision
 * - Min/max validation
 * - Arrow key increment/decrement
 * - Focus: raw number; Blur: formatted
 * - Immediate or onBlur change modes
 * 
 * @param value - Numeric value
 * @param onChange - Change callback
 * @param currency - Currency code
 * @param showCurrencySymbol - Toggle symbol
 * @param maxValue - Upper bound
 * @param minValue - Lower bound
 * @param allowNegative - Enable negatives
 * @param onChangeMode - Change timing
 * @param textFieldProps - TextField props
 * @returns Formatted currency input
 */
const CurrencyInput = forwardRef<HTMLDivElement, CurrencyInputProps>(({
  value = 0,
  onChange,
  currency = 'USD',
  showCurrencySymbol = true,
  maxValue = 999999999.99,
  minValue = 0,
  allowNegative = false,
  onChangeMode = 'onBlur',
  ...textFieldProps
}, ref) => {
  const parseValue = useCallback((str: string): number => {
    const cleaned = str.replace(/[^\d.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  }, []);

  const formatCurrency = useCallback((num: number): string => {
    return isNaN(num) ? '' : num.toFixed(2);
  }, []);

  const initialValue = typeof value === 'string' ? parseValue(value) : (value || 0);

  const [displayValue, setDisplayValue] = useState(() => formatCurrency(initialValue));
  const [focused, setFocused] = useState(false);

  // Sync external value when not focused
  useEffect(() => {
    if (!focused) {
      const parsed = typeof value === 'string' ? parseValue(value) : value ?? 0;
      setDisplayValue(formatCurrency(parsed));
    }
  }, [value, focused, formatCurrency, parseValue]);

  const handleFocus = () => {
    setFocused(true);
    const parsed = typeof value === 'string' ? parseValue(value) : value ?? 0;
    setDisplayValue(parsed === 0 ? '' : parsed.toString());
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseValue(displayValue);

    let clamped = parsed;
    if (parsed < minValue) clamped = minValue;
    if (parsed > maxValue) clamped = maxValue;

    setDisplayValue(formatCurrency(clamped));
    if (onChange && onChangeMode === 'onBlur') {
      onChange(clamped);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
      if (!allowNegative && inputValue.startsWith('-')) return;

      let cleaned = inputValue.replace(/^0+(?!\.)/, '');
      if (/^0*\./.test(cleaned)) {
        cleaned = cleaned.replace(/^0+/, '0');
      }

      setDisplayValue(cleaned);

      if (onChange && onChangeMode === 'immediate') {
        const parsed = parseValue(cleaned);
        if (parsed >= minValue && parsed <= maxValue) {
          onChange(parsed);
        }
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'Home', 'End'
    ];

    if (allowedKeys.includes(event.key)) return;
    if (event.ctrlKey && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) return;
    if (/^\d$/.test(event.key)) return;
    if (event.key === '.' && !displayValue.includes('.')) return;
    if (event.key === '-' && allowNegative && displayValue.length === 0) return;

    event.preventDefault();
  };

  return (
    <TextField
      {...textFieldProps}
      ref={ref}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      inputProps={{
        inputMode: 'decimal',
        pattern: allowNegative ? '-?[0-9]*[.,]?[0-9]*' : '[0-9]*[.,]?[0-9]*',
        'data-raw-value': parseValue(displayValue),
        style: { textAlign: 'right' },
        ...textFieldProps.inputProps,
      }}
      InputProps={{
        startAdornment: showCurrencySymbol ? (
          <InputAdornment position="start">
            <AttachMoneyIcon />
          </InputAdornment>
        ) : undefined,
        ...textFieldProps.InputProps,
      }}
    />
  );
});

CurrencyInput.displayName = 'CurrencyInput';

export default CurrencyInput;